import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CreditCard, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { shopAPI } from '../lib/api';
import { t } from '../lib/i18n';
import { Price } from '../components/CourseCard';
import { EmptyState, btnPrimary, panelCls } from '../components/ui';
import CouponField, { useReleaseOnCancel } from '../components/CouponField';

export const notifyCartChanged = () => window.dispatchEvent(new Event('lms-cart-changed'));

export default function CartPage() {
  const [cart, setCart] = useState(null);
  const [busy, setBusy] = useState(false);
  const [coupon, setCoupon] = useState(null); // s136: validated coupon preview
  const navigate = useNavigate();
  useReleaseOnCancel();

  const load = useCallback(() => {
    shopAPI.cart().then(({ data }) => setCart(data)).catch(() => setCart({ courses: [], total: 0 }));
    setCoupon(null); // cart changed → re-apply against the new contents
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const remove = async (cid) => {
    try {
      await shopAPI.cartRemove(cid);
      notifyCartChanged();
      load();
    } catch {
      toast.error(t('Save failed'));
    }
  };

  const checkout = async () => {
    setBusy(true);
    try {
      const { data } = await shopAPI.checkout(cart.courses.map((c) => c.id), coupon ? coupon.code : '', '/cart');
      if (data.free) { // 100 % coupon: enrolled without Stripe
        notifyCartChanged();
        toast.success(t('You are enrolled!'));
        navigate('/my/courses');
        return;
      }
      window.location.assign(data.url); // Stripe-hosted checkout
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Checkout failed'));
      setBusy(false);
    }
  };

  if (!cart) return <p className="text-sm text-lms-muted">{t('Loading…')}</p>;

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="mb-5 text-lg font-semibold">{t('Cart')}</h1>
      {cart.courses.length === 0 ? (
        <EmptyState>
          {t('Your cart is empty.')}{' '}
          <Link to="/courses" className="text-lms-accent">{t('Browse courses')}</Link>
        </EmptyState>
      ) : (
        <div className="space-y-4">
          <div className={`${panelCls} divide-y divide-[var(--lms-border)]`}>
            {cart.courses.map((c) => (
              <div key={c.id} className="flex items-center gap-4 p-4">
                {c.thumbnail ? (
                  <img src={c.thumbnail} alt="" className="h-12 w-20 rounded object-cover" />
                ) : (
                  <span className="flex h-12 w-20 items-center justify-center rounded bg-lms-raised text-xs text-lms-faint">—</span>
                )}
                <div className="min-w-0 flex-1">
                  <Link to={`/course/${c.slug}`} className="text-sm font-medium hover:text-lms-accent">{c.title}</Link>
                </div>
                <Price course={c} />
                <button onClick={() => remove(c.id)} className="text-lms-muted hover:text-[var(--lms-danger)]" title={t('Remove')}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
          <div className={`${panelCls} flex flex-col gap-4 p-5`}>
            <CouponField courseIds={cart.courses.map((c) => c.id)} applied={coupon} onApplied={setCoupon} />
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-xs text-lms-muted">{t('Total')}</div>
                <div className="lms-num">
                  {coupon && <span className="mr-2 text-sm text-lms-faint line-through">${cart.total.toFixed(2)}</span>}
                  <span className="text-xl font-semibold">${(coupon ? coupon.total : cart.total).toFixed(2)}</span>
                </div>
                {coupon && coupon.discount > 0 && (
                  <div className="text-xs text-lms-sub lms-num">{t('You save')} ${coupon.discount.toFixed(2)}</div>
                )}
              </div>
              <button onClick={checkout} disabled={busy} className={btnPrimary}>
                <CreditCard size={15} />
                {busy ? t('Redirecting…') : coupon && coupon.total <= 0 ? t('Enroll now') : t('Checkout with Stripe')}
              </button>
            </div>
          </div>
          <p className="text-xs text-lms-faint">{t('Payment is handled on Stripe’s secure page — card details never touch this site.')}</p>
        </div>
      )}
    </div>
  );
}
